// GlobalAiConsentGate — shown once to every authenticated user before any
// AI feature can transmit data to a third-party provider.
//
// Apple 5.1.1(i) / 5.1.2(i) requires:
//   • Disclose what data is sent
//   • Identify who it goes to (every provider)
//   • Obtain permission BEFORE sending
//
// This gate satisfies all three for every one of the 24 AI-powered features
// in the app with a single, comprehensive disclosure.

import { createPortal } from "react-dom";
import { useState } from "react";
import { motion } from "framer-motion";
import { useAuthStore } from "@/store/auth-store";
import { useLocation, useNavigate } from "react-router-dom";
import {
  NavSparkle as Sparkles, NavBot, NavBook, NavReport, NavTimetable, NavEvents, NavClipboard,
  NavExam, NavChat, NavAward, NavChart,
} from "@/components/icons/solid-icons";
import { ShieldFill as Shield, GradCapFill } from "@/components/icons/solid-extra-icons";
import { nativeTone } from "@/lib/native-tone";
import { useTranslation } from "react-i18next";

// NOTE: bumped v1 → v2 on 2026-07-02. WKWebView localStorage survives
// in-place App Store / TestFlight updates, so anyone who accepted the
// old key never saw the gate again — including Apple's reviewer, which
// is why review kept reporting "no AI consent shown" (5.1.1(i)/5.1.2(i)).
// Bumping the key forces a one-time re-prompt for every existing install
// so the disclosure is unmissable on the next binary.
export const AI_CONSENT_KEY = "campushub_ai_consent_v2";

// Public paths that never need the gate (user is not yet authenticated there)
const PUBLIC_PREFIXES = ["/login", "/forgot-password", "/reset-password", "/invitations/accept", "/legal", "/site-preview", "/auth/handoff"];

function isPublicPath(pathname: string): boolean {
  if (pathname === "/") return true;
  return PUBLIC_PREFIXES.some((p) => pathname.startsWith(p));
}

export function readAiConsent(): "accepted" | "declined" | null {
  try {
    const v = localStorage.getItem(AI_CONSENT_KEY);
    if (v === "accepted" || v === "declined") return v;
  } catch { /* ignore */ }
  return null;
}

export function writeAiConsent(v: "accepted" | "declined") {
  try {
    localStorage.setItem(AI_CONSENT_KEY, v);
    // Keep the Hubba-specific key in sync for belt-and-suspenders
    localStorage.setItem("hubba_ai_consent_v1", v);
  } catch { /* ignore */ }
}

export function clearAiConsent() {
  try {
    localStorage.removeItem(AI_CONSENT_KEY);
    localStorage.removeItem("hubba_ai_consent_v1");
  } catch { /* ignore */ }
}

// English text lives beside each key as the pre-load / missing-key fallback;
// the rendered copy comes from `common:aiConsent.*` so a Hindi or Telugu
// reader gets this disclosure — the first screen after sign-in — in their
// own language. Provider names are brand names and stay as-is.
const PROVIDERS = [
  { id: "claude", name: "Anthropic Claude", role: "Primary AI — powers Hubba, report card comments, AI tutor, timetable, year plan, and all generative features.", dot: "#8B5CF6" },
  { id: "openai", name: "OpenAI", role: "Embeddings — powers semantic search across notices, lessons, and knowledge base articles.", dot: "#10A37F" },
  { id: "lavekai", name: "LavekAI", role: "Runs the AI Teacher's live lessons (board, voice, questions) using its own AI providers.", dot: "#0EA08F" },
  { id: "fallback", name: "Google Gemini / Groq / DeepSeek / GLM", role: "Fallback cascade — activated automatically only if the primary provider is temporarily unavailable.", dot: "#4285F4" },
];

const ROLES = {
  all: "All users",
  studentsParents: "Students, Parents",
  teachersPrincipalsAdmins: "Teachers, Principals, Admins",
  adminsPrincipals: "Admins, Principals",
  teachersAdmins: "Teachers, Admins",
  teachers: "Teachers",
} as const;

const FEATURES: { id: string; icon: typeof NavBot; label: string; roles: keyof typeof ROLES; data: string }[] = [
  { id: "hubba", icon: NavBot, label: "Hubba AI Assistant", roles: "all", data: "Your messages, name, role, school data you ask about" },
  { id: "tutor", icon: NavBook, label: "AI Tutor & Study Plans", roles: "studentsParents", data: "Student name, exam history, subject performance" },
  { id: "aiTeacher", icon: NavBook, label: "AI Teacher (live lessons)", roles: "all", data: "Your name, school and class, and what you say or type in the lesson" },
  { id: "career", icon: GradCapFill, label: "Career Recommendations", roles: "studentsParents", data: "Student interests, academic stream, grades" },
  { id: "reportComments", icon: NavReport, label: "Report Card Comments", roles: "teachersPrincipalsAdmins", data: "Student exam results, student names" },
  { id: "timetable", icon: NavTimetable, label: "Timetable AI Generation", roles: "adminsPrincipals", data: "Teacher names, subject codes, availability, building layout" },
  { id: "yearPlan", icon: NavEvents, label: "Curriculum Year Plan", roles: "teachersAdmins", data: "Chapter titles, topic counts, holiday schedule" },
  { id: "lessonLog", icon: NavClipboard, label: "Lesson Log AI Draft", roles: "teachers", data: "Lesson description, class and subject name" },
  { id: "admissionTest", icon: NavExam, label: "Admission Test Generation", roles: "adminsPrincipals", data: "Grade level, subject, difficulty setting" },
  { id: "drafts", icon: NavChat, label: "Forum & Announcement AI Drafts", roles: "adminsPrincipals", data: "Your draft text, school name" },
  { id: "studio", icon: NavAward, label: "Studio ID Card / Certificate AI", roles: "adminsPrincipals", data: "Design description, template layout" },
  { id: "insights", icon: NavChart, label: "Dashboard Insights & GL Analysis", roles: "adminsPrincipals", data: "Aggregated school metrics, financial summaries" },
  { id: "pdfImport", icon: NavReport, label: "Curriculum PDF Import", roles: "teachersAdmins", data: "Uploaded textbook PDF or image" },
];

function ConsentModal({ onAccept, onDecline, onPrivacy }: { onAccept: () => void; onDecline: () => void; onPrivacy: () => void }) {
  const [tab, setTab] = useState<"features" | "providers">("features");
  const { t: tr } = useTranslation();
  const c = (key: string, en: string, params?: Record<string, unknown>) =>
    tr(`common:aiConsent.${key}`, { defaultValue: en, ...params });

  return createPortal(
      <div role="dialog" aria-modal="true" aria-labelledby="ai-consent-title" className="ch-native-consent">
        <motion.button
          type="button"
          className="ch-native-consent__backdrop"
          aria-label={c("background", "Background")}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.22 }}
        />
        <motion.div
          className="ch-native-consent__card"
          initial={{ y: "105%" }}
          animate={{ y: 0 }}
          transition={{ type: "spring", damping: 32, stiffness: 340, mass: 0.85 }}
        >
          <div className="ch-native-consent__handle" aria-hidden />
          <header className="ch-native-consent__header">
            <div className="ch-native-consent__icon">
              <Shield size={22} />
            </div>
            <div>
              <p className="ch-native-consent__eyebrow">
                <Sparkles size={11} /> {c("eyebrow", "AI & Privacy")}
              </p>
              <h2 id="ai-consent-title" className="ch-native-consent__title">
                {c("title", "AI features use trusted providers")}
              </h2>
              <p className="ch-native-consent__lead">
                {c("lead", "CampusHub uses AI to power tutoring, report cards, timetables and Hubba. Review what's shared before you continue — nothing is sent until you accept.")}
              </p>
            </div>
          </header>

          <div className="ch-native-consent__tabs">
            {(["features", "providers"] as const).map((t) => (
              <button
                key={t}
                type="button"
                className={`ch-native-consent__tab${tab === t ? " ch-native-consent__tab--active" : ""}`}
                onClick={() => setTab(t)}
              >
                {t === "features"
                  ? c("featuresTab", `Features (${FEATURES.length})`, { n: FEATURES.length })
                  : c("providersTab", `Providers (${PROVIDERS.length})`, { n: PROVIDERS.length })}
              </button>
            ))}
          </div>

          <div className="ch-native-consent__body">
            {tab === "features" && FEATURES.map((f, i) => (
              <div key={f.id} className="ch-native-consent__row">
                <span className={`ch-native-consent__glyph ${nativeTone(i)}`} aria-hidden>
                  <f.icon size={16} />
                </span>
                <div>
                  <p className="ch-native-consent__row-title">
                    {c(`f.${f.id}.label`, f.label)}
                    <span className="ch-native-consent__badge">{c(`roles.${f.roles}`, ROLES[f.roles])}</span>
                  </p>
                  <p className="ch-native-consent__row-sub">{c("sends", `Sends: ${f.data}`, { data: c(`f.${f.id}.data`, f.data) })}</p>
                </div>
              </div>
            ))}
            {tab === "providers" && PROVIDERS.map((p) => (
              <div key={p.name} className="ch-native-consent__row">
                <span className="ch-native-consent__dot" style={{ background: p.dot }} />
                <div>
                  <p className="ch-native-consent__row-title">{p.name}</p>
                  <p className="ch-native-consent__row-sub">{c(`p.${p.id}`, p.role)}</p>
                </div>
              </div>
            ))}
          </div>

          <footer className="ch-native-consent__footer">
            <p className="ch-native-consent__legal">
              {c("legalBefore", "Providers don't train on your data. Read the")}{" "}
              <span
                role="link"
                tabIndex={0}
                onClick={onPrivacy}
                onKeyDown={(e) => { if (e.key === "Enter") onPrivacy(); }}
                className="ch-native-consent__link"
              >
                {c("privacyPolicy", "CampusHubAI Privacy Policy")}
              </span>
              {c("legalAfter", ". Withdraw anytime in Settings → Privacy.")}
            </p>
            <button type="button" className="ch-campus-btn" onClick={onAccept}>
              {c("accept", "Accept & Continue")}
            </button>
            <button type="button" className="ch-native-consent__secondary" onClick={onDecline}>
              {c("decline", "No thanks — disable AI features")}
            </button>
          </footer>
        </motion.div>
      </div>,
      document.body,
    );
}

export function GlobalAiConsentGate({ children }: { children: React.ReactNode }) {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const location = useLocation();
  const navigate = useNavigate();
  const [consent, setConsent] = useState<"accepted" | "declined" | null>(() => readAiConsent());

  // Don't gate public pages or unauthenticated users
  if (!isAuthenticated || isPublicPath(location.pathname)) {
    return <>{children}</>;
  }

  // Consent already given — let the app through unchanged
  if (consent !== null) {
    return <>{children}</>;
  }

  // Consent is still pending: render ONLY the modal — no children mount, no
  // hooks run, no network calls fire. This guarantees zero data reaches any
  // third-party AI provider before the user makes an explicit choice.
  return (
    <ConsentModal
      onPrivacy={() => navigate("/legal/privacy#ai")}
      onAccept={() => {
        writeAiConsent("accepted");
        setConsent("accepted");
      }}
      onDecline={() => {
        writeAiConsent("declined");
        setConsent("declined");
      }}
    />
  );
}
